import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '../hooks/useToast';
import { useAuthStore } from '../store/authStore';
import { userService } from '../services/user.service';
import { divisionService } from '../services/division.service';
import { getErrorMessage } from '../lib/api';
import { UserPlus, X } from 'lucide-react';
import type { User, CrmRole } from '../types';
import type { Division } from '../types/crm';
import Loading from '../components/Loading';
import { getProfilePictureUrl } from '../utils/avatar';
import { useFeatureFlag } from '../lib/featureFlags';

const CRM_ROLE_LABELS: Record<CrmRole, string> = {
  rep: 'Sales rep',
  manager: 'Manager',
  ceo: 'CEO',
  client_manager: 'Client manager',
};

const CRM_ROLE_OPTIONS: Array<{ value: '' | CrmRole; label: string }> = [
  { value: '', label: 'No CRM role' },
  { value: 'rep', label: CRM_ROLE_LABELS.rep },
  { value: 'manager', label: CRM_ROLE_LABELS.manager },
  { value: 'ceo', label: CRM_ROLE_LABELS.ceo },
  { value: 'client_manager', label: CRM_ROLE_LABELS.client_manager },
];

const createUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  role: z.enum(['regular_user', 'account_admin']),
  crm_role: z.enum(['', 'rep', 'manager', 'ceo', 'client_manager']).optional(),
  division_id: z.string().optional(),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

export const AdminUsers = () => {
  const currentUser = useAuthStore((state) => state.user);
  // CRM role and division only mean something while the CRM is on.
  const crmEnabled = useFeatureFlag('crm');
  const [users, setUsers] = useState<User[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      role: 'regular_user',
      crm_role: '',
      division_id: '',
    },
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [fetchedUsers, divisionsResult] = await Promise.all([
        userService.getAllUsers(),
        crmEnabled
          ? divisionService.getAll({ per_page: 100 }).catch(() => ({ data: [], meta: undefined }))
          : Promise.resolve({ data: [], meta: undefined }),
      ]);
      setUsers(fetchedUsers);
      setDivisions(divisionsResult.data as Division[]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onCreateUser = async (data: CreateUserFormData) => {
    setIsCreating(true);
    try {
      await userService.createUser({
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        role: data.role,
        ...(crmEnabled && {
          crm_role: data.crm_role ? (data.crm_role as CrmRole) : null,
          division_id: data.division_id ? Number(data.division_id) : null,
        }),
      });
      toast.success('User created. A welcome email has been sent.');
      setShowCreateModal(false);
      reset();
      loadAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateCrmRole = async (user: User, newRole: '' | CrmRole) => {
    try {
      const updatedUser = await userService.updateUserAdmin(user.id, {
        crm_role: newRole === '' ? null : newRole,
      });
      setUsers(users.map((u) => (u.id === user.id ? updatedUser : u)));
      toast.success('CRM role updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateDivision = async (user: User, newDivisionId: string) => {
    try {
      const updatedUser = await userService.updateUserAdmin(user.id, {
        division_id: newDivisionId === '' ? null : Number(newDivisionId),
      });
      setUsers(users.map((u) => (u.id === user.id ? updatedUser : u)));
      toast.success('Division updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      const updatedUser = await userService.toggleActive(user.id);
      setUsers(users.map(u => u.id === user.id ? updatedUser : u));
      toast.success(`User ${updatedUser.active ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Users created by their first Microsoft sign-in wait for this (ENG-746).
  const handleGrantAccess = async (user: User) => {
    try {
      const updatedUser = await userService.updateUserAdmin(user.id, { access_pending: false });
      setUsers(users.map((u) => (u.id === user.id ? updatedUser : u)));
      toast.success(`${updatedUser.full_name} now has access`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateRole = async (user: User, newRole: 'regular_user' | 'account_admin') => {
    try {
      const updatedUser = await userService.updateRole(user.id, newRole);
      setUsers(users.map(u => u.id === user.id ? updatedUser : u));
      toast.success('User role updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Users</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage users in your account
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <UserPlus className="h-4 w-4 inline mr-2" />
          Add User
        </button>
      </div>

      {/* Users table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                {crmEnabled && (
                  <>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CRM role
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Division
                    </th>
                  </>
                )}
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={getProfilePictureUrl(user.profile_picture_url)}
                        alt={user.full_name}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.full_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateRole(user, e.target.value as any)}
                      disabled={user.id === currentUser?.id}
                      title={user.id === currentUser?.id ? "You cannot change your own role" : undefined}
                      className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                      <option value="regular_user">Regular User</option>
                      <option value="account_admin">Account Admin</option>
                    </select>
                  </td>
                  {crmEnabled && (
                  <>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.crm_role ?? ''}
                      onChange={(e) => handleUpdateCrmRole(user, e.target.value as '' | CrmRole)}
                      className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      aria-label={`CRM role for ${user.full_name}`}
                    >
                      {CRM_ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.division?.id ?? ''}
                      onChange={(e) => handleUpdateDivision(user, e.target.value)}
                      className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      aria-label={`Division for ${user.full_name}`}
                    >
                      <option value="">No division</option>
                      {divisions.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </td>
                  </>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                    {user.access_pending && (
                      <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Waiting for access
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {user.access_pending && (
                      <button
                        onClick={() => handleGrantAccess(user)}
                        className="mr-4 text-primary-600 hover:text-primary-900"
                      >
                        Grant access
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`${
                        user.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                      }`}
                    >
                      {user.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setShowCreateModal(false)}
            />

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreateUser)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Add New User
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="first_name" className="label">First Name</label>
                        <input
                          {...register('first_name')}
                          id="first_name"
                          type="text"
                          className="input"
                        />
                        {errors.first_name && (
                          <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="last_name" className="label">Last Name</label>
                        <input
                          {...register('last_name')}
                          id="last_name"
                          type="text"
                          className="input"
                        />
                        {errors.last_name && (
                          <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="label">Email</label>
                      <input
                        {...register('email')}
                        id="email"
                        type="email"
                        className="input"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="role" className="label">Role</label>
                      <select {...register('role')} id="role" className="input">
                        <option value="regular_user">Regular User</option>
                        <option value="account_admin">Account Admin</option>
                      </select>
                      {errors.role && (
                        <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                      )}
                    </div>

                    {crmEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="crm_role" className="label">CRM role</label>
                        <select {...register('crm_role')} id="crm_role" className="input">
                          {CRM_ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="division_id" className="label">Division</label>
                        <select {...register('division_id')} id="division_id" className="input">
                          <option value="">No division</option>
                          {divisions.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="btn btn-primary"
                  >
                    {isCreating ? 'Creating...' : 'Create User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
